import React, { useState, useRef, useEffect } from 'react';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { useDivisionContext } from '../../contexts/division-context';
import { cn } from '../../utils';

export function DivisionSelector() {
  const {
    selectedDivisionId,
    setSelectedDivisionId,
    divisions,
    currentDivision,
    shouldShowDivisionSelector,
    hasMultipleDivisions,
    isLoadingDivisions,
  } = useDivisionContext();

  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debug logging
  useEffect(() => {
    console.log('[DivisionSelector] Rendering with:', {
      shouldShowDivisionSelector,
      hasMultipleDivisions,
      divisionsLength: divisions.length,
      isLoadingDivisions,
      selectedDivisionId,
      currentDivision: currentDivision?.name,
      divisions: divisions.map(d => ({ id: d.id, name: d.name })),
    });
  }, [shouldShowDivisionSelector, hasMultipleDivisions, divisions, isLoadingDivisions, selectedDivisionId, currentDivision]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Don't show selector if user only has access to one division in their JWT token
  // Even if some divisions in JWT don't exist in database, we show the selector if JWT has multiple
  if (!hasMultipleDivisions) {
    // Still show current division, but not as a dropdown
    if (currentDivision) {
      return (
        <div className="flex items-center space-x-2 px-3 py-2 bg-gray-50 dark:bg-secondary-900 rounded-lg border border-gray-200 dark:border-secondary-700">
          <Building2 className="h-4 w-4 text-gray-500 dark:text-secondary-400" />
          <div className="text-sm">
            <p className="font-medium text-gray-700 dark:text-secondary-200">{currentDivision.name}</p>
            {currentDivision.code && (
              <p className="text-xs text-gray-500 dark:text-secondary-400">{currentDivision.code}</p>
            )}
          </div>
        </div>
      );
    }
    return null;
  }

  if (isLoadingDivisions) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 bg-gray-50 dark:bg-secondary-900 rounded-lg border border-gray-200 dark:border-secondary-700">
        <Building2 className="h-4 w-4 text-gray-500 dark:text-secondary-400 animate-pulse" />
        <span className="text-sm text-gray-500 dark:text-secondary-400">Loading divisions...</span>
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 bg-white dark:bg-secondary-800 hover:bg-gray-50 dark:hover:bg-secondary-700 rounded-lg border border-gray-200 dark:border-secondary-700 transition-colors"
      >
        <Building2 className="h-4 w-4 text-gray-500 dark:text-secondary-400" />
        <div className="text-left">
          <p className="text-sm font-medium text-gray-700 dark:text-secondary-200">
            {currentDivision?.name || 'Select Division'}
          </p>
          {currentDivision?.code && (
            <p className="text-xs text-gray-500 dark:text-secondary-400">{currentDivision.code}</p>
          )}
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-gray-500 dark:text-secondary-400 transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute left-0 mt-2 w-64 bg-white dark:bg-secondary-800 rounded-lg shadow-lg border border-gray-200 dark:border-secondary-700 py-1 z-50 max-h-96 overflow-y-auto">
          {divisions.map((division) => (
            <button
              key={division.id}
              onClick={() => {
                setSelectedDivisionId(division.id);
                setIsOpen(false);
              }}
              className={cn(
                'flex items-center justify-between w-full px-4 py-2 text-sm transition-colors',
                division.id === selectedDivisionId
                  ? 'bg-blue-50 dark:bg-blue-950 text-blue-700'
                  : 'text-gray-700 dark:text-secondary-200 hover:bg-gray-50 dark:hover:bg-secondary-700'
              )}
            >
              <div className="flex-1 text-left">
                <p className="font-medium">{division.name}</p>
                {division.code && (
                  <p className="text-xs text-gray-500 dark:text-secondary-400">{division.code}</p>
                )}
                {division.description && (
                  <p className="text-xs text-gray-500 dark:text-secondary-400 mt-1 line-clamp-1">
                    {division.description}
                  </p>
                )}
              </div>
              {division.id === selectedDivisionId && (
                <Check className="h-4 w-4 text-blue-700 ml-2 flex-shrink-0" />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
